import type { IFilter, ISectionedFilters, TFlatFiltersBody, TSectionedFiltersBody } from "types/filters";
import type { TPaginationSortFilterOptions, TPaginationSortOptions } from "types/pagination";
import type { IPaginatedSearchOptions } from "utils/searchUtils";

import { notEmpty } from "utils/comparison";

export function addSearchParam(searchParams: URLSearchParams, search: string): URLSearchParams {
  const normalizedSearch = search.trim();
  if (normalizedSearch) {
    searchParams.set("search", normalizedSearch);
  }
  return searchParams;
}

export function filtersToRecord(
  filters: IFilter[],
): TFlatFiltersBody {
  const result: TFlatFiltersBody = {};
  for (const filter of filters) {
    const name = getContextedFilterName(filter);
    result[name] = {
      operator: filter.operator,
      value: filter.value,
      ...(filter.relation && { relation: filter.relation }),
    };
  }
  return result;
}

export function fromFilters(filters: IFilter[]): URLSearchParams {
  return mergeURLSearchParams(
    filters
      .filter((filter) => filter.toServerURLSearchParams || filter.toURLSearchParams)
      .map((filter) => (filter.toServerURLSearchParams ? filter.toServerURLSearchParams() : filter.toURLSearchParams())),
  );
}

export function fromPaginationSortFilterOptions(
  options?: TPaginationSortFilterOptions & { withUnmanaged?: boolean },
): URLSearchParams {
  if (!options) {
    return new URLSearchParams();
  }
  const searchParams = fromPaginationSortOptions(options);
  const filterParams = fromFilters(options.filters || []);
  const withUnmanagedParams = new URLSearchParams(
    options.withUnmanaged !== undefined ? { withUnmanaged: String(options.withUnmanaged) } : {},
  );
  return mergeURLSearchParams([searchParams, filterParams, withUnmanagedParams]);
}

export function fromPaginationSortOptions(options: TPaginationSortOptions): URLSearchParams {
  const searchParams = new URLSearchParams();
  if (options.page) {
    searchParams.append("page", options.page.toString());
  }
  if (options.perPage) {
    searchParams.append("perPage", options.perPage.toString());
  }
  if (options.order) {
    searchParams.append("sortOrder", options.order);
  }
  if (options.sortFields) {
    const fields = Array.isArray(options.sortFields) ? options.sortFields : [options.sortFields];
    fields.forEach((field) => searchParams.append("sortFields", field));
  }
  return searchParams;
}

export function mergeURLSearchParams(searchParams: URLSearchParams[]): URLSearchParams {
  const mergedSearchParams = new URLSearchParams();
  searchParams.forEach((params) => {
    for (const [key, value] of params) {
      mergedSearchParams.append(key, value);
    }
  });
  return mergedSearchParams;
}

export function paginatedSearchOptionsToBody(
  paginationOptions?: IPaginatedSearchOptions,
  filters?: IFilter[] | ISectionedFilters,
  useLegacyFormat = true,
): Record<string, unknown> {
  return paginationSortFilterToBody(
    {
      filters,
      order: paginationOptions?.sort?.order,
      page: paginationOptions?.pagination?.page,
      perPage: paginationOptions?.pagination?.perPage,
      search: paginationOptions?.search?.query,
      sortFields: paginationOptions?.sort?.sortFields,
    },
    useLegacyFormat,
  );
}

export function paginationSortFilterToBody(
  options: TPaginationSortOptions & { filters?: IFilter[] | ISectionedFilters; search?: string },
  useLegacyFormat = true,
): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (options.page !== undefined) body.page = options.page;
  if (options.perPage !== undefined) body.perPage = options.perPage;
  if (options.order) body.sortOrder = options.order;
  if (options.sortFields) body.sortFields = Array.isArray(options.sortFields) ? options.sortFields : [options.sortFields];
  if (options.search) body.search = options.search.trim();
  if (options.filters) body.filters = sectionedFiltersToBody(options.filters, useLegacyFormat);
  return body;
}

export function sectionedFiltersToBody(model: IFilter[] | ISectionedFilters, useLegacyFormat = true): TFlatFiltersBody | TSectionedFiltersBody {
  if (Array.isArray(model)) {
    return filtersToRecord(model);
  }

  const sections = model.sections.map((section) => {
    const validFilters = section.filters
      .valueSeq()
      .filter(({ isValid }) => isValid)
      .map(({ filter }) => filter)
      .toArray();
    if (!validFilters.length) return undefined;
    return {
      filters: filtersToRecord(validFilters),
      relation: section.relation,
    };
  }).filter(notEmpty).toArray();

  // Single section with "and" relation — send flat format for backward compatibility
  if (useLegacyFormat && (sections.length <= 1 && (sections.length === 0 || sections[0].relation === "and"))) {
    return sections[0]?.filters ?? {};
  }

  return {
    relation: model.relation,
    sections,
  };
}

function getContextedFilterName(filter: IFilter): string {
  const context = (filter.constructor as { filterContext?: string }).filterContext;
  return context ? `${context}${filter.name.charAt(0).toLocaleUpperCase()}${filter.name.slice(1)}` : filter.name;
}
